import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { stripeAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      setLoading(false);
      return;
    }

    try {
      const response = await stripeAPI.getCheckoutStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setLoading(false);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setStatus('error');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200 shadow-lg">
        <CardContent className="p-8 text-center">
          {loading ? (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto" />
              <h2 className="mt-4 text-xl font-bold text-slate-900">Processing Payment...</h2>
              <p className="mt-2 text-slate-500">Please wait while we confirm your subscription.</p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-slate-900">Payment Successful!</h2>
              <p className="mt-2 text-slate-500">Your subscription is now active. Welcome to ReStaff!</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="mt-6 w-full bg-orange-500 hover:bg-orange-600"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-slate-900">Payment Issue</h2>
              <p className="mt-2 text-slate-500">
                {status === 'timeout' && 'Payment verification timed out. Please check your email for confirmation.'}
                {status === 'expired' && 'Payment session expired. Please try again.'}
                {status === 'error' && 'Something went wrong. Please contact support.'}
              </p>
              <Button
                onClick={() => navigate('/settings')}
                className="mt-6 w-full"
                variant="outline"
              >
                Go to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function SubscriptionCancel() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="h-10 w-10 text-slate-500" />
          </div>
          <h2 className="mt-4 text-xl font-bold text-slate-900">Subscription Canceled</h2>
          <p className="mt-2 text-slate-500">
            You've canceled the subscription process. You can try again anytime from the settings page.
          </p>
          <div className="mt-6 space-y-3">
            <Button
              onClick={() => navigate('/settings')}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              Go to Settings
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
